import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import {
  useGetUserGroupMembershipLazyQuery,
  useUpsertUsersGroupMembershipMutation,
} from 'types/generated/client';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import { Button, ButtonLink } from 'components/Button';
import ModalGroup from 'components/modals/ModalGroup';

interface Props {
  actionUrl?: string;
  actionHandler?: () => void;
  groupId: string;
  isPrivate?: boolean;
  fetchData?: () => void;
  clickedSignUp?: () => void;
}

export default function JoinGroup({
  actionUrl,
  actionHandler,
  groupId,
  isPrivate = false,
  fetchData,
  clickedSignUp,
}: Props) {
  const viewer = useViewer();
  const { isAnonymousSession } = useViewer();
  const [isMember, setIsMember] = useState(false);
  const viewerId = viewer.userId;

  const {
    isOpen: isPrivateGroupModalOpen,
    openModal: openPrivateGroupModal,
    closeModal: closePrivateGroupModal,
  } = useModal();

  const [getUserGroupMembershipLazyQuery, { data: userMembershipData }] =
    useGetUserGroupMembershipLazyQuery();

  const [upsertUsersGroupMembershipMutation, { data: updatedGroupStatus }] =
    useUpsertUsersGroupMembershipMutation();

  const fetchMembershipStatus = () => {
    if (viewerId && groupId)
      getUserGroupMembershipLazyQuery({
        fetchPolicy: 'network-only',
        variables: {
          userId: viewerId,
          groupId: groupId,
        },
      });
  };

  useEffect(() => {
    fetchMembershipStatus();
  }, [groupId, viewerId]);

  useEffect(() => {
    if (userMembershipData?.groupMembers?.length && userMembershipData?.groupMembers[0]?.isActive) {
      setIsMember(true);
    } else {
      setIsMember(false);
    }
  }, [userMembershipData]);

  useEffect(() => {
    if (updatedGroupStatus?.insertGroupMembersOne) {
      fetchData && fetchData();
      setIsMember(updatedGroupStatus?.insertGroupMembersOne?.isActive);
    }
  }, [updatedGroupStatus]);

  const handleJoinGroup = async () => {
    if (isAnonymousSession) {
      clickedSignUp && clickedSignUp();
      return;
    }

    if (!viewerId || !groupId) return;

    if (isPrivate) {
      openPrivateGroupModal();
      return;
    }

    try {
      await upsertUsersGroupMembershipMutation({
        variables: {
          userId: viewerId,
          groupId: groupId,
          isActive: true,
        },
        optimisticResponse: {
          __typename: 'mutation_root',
          insertGroupMembersOne: {
            __typename: 'GroupMembers',
            userId: viewerId,
            groupId: groupId,
            isActive: true,
          },
        },
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  if (isMember) {
    return (
      <Button variant="secondary" size="lg">
        Joined
      </Button>
    );
  }

  const joinButtonText = isPrivate ? 'Request to join' : 'Join the group';

  return (
    <div className="w-full">
      <Button
        variant="brand"
        size="lg"
        className="w-full"
        onClick={() => {
          if (isAnonymousSession) {
            clickedSignUp && clickedSignUp();
          } else {
            handleJoinGroup();
          }
        }}
      >
        <div className="flex items-center justify-center">{joinButtonText}</div>
      </Button>

      <ModalGroup
        isOpen={isPrivateGroupModalOpen}
        closeModal={closePrivateGroupModal}
        title="Request to join group"
        description="This is a private group. Comments 
and posts are restricted."
      />
    </div>
  );
}
